/**
 * Play Actions (CONTEXT.md, "Play Action"): every change to a Session, applied by
 * `applyPlayAction`, which returns the new Session, the inverse Play Action and the events worth
 * logging. Primitive actions set one thing and invert exactly; the table actions (damage, apply
 * a Condition, advance time, daily preparations ...) expand into primitives, so their inverse is
 * the reversed sequence of primitive inverses and reversal is exact by construction. The Roll
 * Log (#20) records the action and its inverse.
 */
import type { ToggleState } from "../character/model";
import type { RollRecord } from "../rolls/model";
import { summarizeRoll } from "../rolls/model";
import type { DamageInstanceInput } from "./iwr";
import { applyIWR, describeApplication } from "./iwr";
import type { SessionMaxima } from "./maxima";
import { freshSession } from "./maxima";
import type { ConsumableStack, EffectDuration, PersistentDamage, Session, SessionCondition, SessionEffect, SpellcastingUse } from "./model";
import { DEATH_CAPS, HERO_POINTS_MAX, REST_SECONDS, ROUND_SECONDS, conditionKey, conditionValue, findCondition, findEffect, isLocked, remainingDuration } from "./model";

/* --------------------------------- Action types --------------------------------- */

export type PrimitiveAction =
  | { kind: "set-hp"; value: number; temp: number }
  | { kind: "add-condition"; condition: SessionCondition; index: number }
  | { kind: "remove-condition"; id: string }
  | { kind: "set-condition-value"; id: string; value: number | null }
  | { kind: "add-effect"; effect: SessionEffect; index: number }
  | { kind: "remove-effect"; id: string }
  | { kind: "set-effect"; id: string; effect: SessionEffect }
  | { kind: "set-hero-points"; value: number }
  | { kind: "set-focus"; value: number }
  /** A null value removes the key (a resource the maxima no longer list). */
  | { kind: "set-resource"; key: string; value: number | null }
  | { kind: "set-uses"; uuid: string; value: number }
  | { kind: "set-slots"; entry: string; rank: string; expended: number }
  | { kind: "set-prepared"; entry: string; rank: string; index: number; expended: boolean }
  | { kind: "set-innate"; entry: string; spell: string; expended: number }
  /** A staff Entry's charges expended since daily preparations (#21). */
  | { kind: "set-charges"; entry: string; expended: number }
  | { kind: "add-consumable"; stack: ConsumableStack; index: number }
  | { kind: "remove-consumable"; id: string }
  | { kind: "set-consumable"; id: string; quantity: number; uses: number | null }
  | { kind: "set-clock"; clock: number; round: number | null }
  | { kind: "set-toggle"; key: string; state: ToggleState | null }
  | { kind: "set-counter"; value: number }
  | { kind: "replace"; session: Session }
  | { kind: "batch"; label: string; steps: PlayAction[] };

/** What happens at the table. Each expands into primitives. */
export type TableAction =
  /**
   * Damage taken (#22 applies the sheet's IWR): `amount` before them, of `type` (untyped when
   * absent) or as the typed `instances` of a Roll, described to the IWR by `options`
   * (`item:magical`, `item:trait:holy`); `critical` marks a critical hit for dying.
   */
  | { kind: "damage"; amount: number; critical?: boolean; source?: string; type?: string; instances?: DamageInstanceInput[]; options?: string[] }
  | { kind: "heal"; amount: number; source?: string }
  | { kind: "set-temp-hp"; value: number; source?: string }
  /** Apply a Condition: a new one at `value` (or 1), or raise an existing valued one by `value` (or 1); `set` makes `value` absolute. */
  | { kind: "apply-condition"; slug: string; value?: number; set?: boolean; persistent?: PersistentDamage }
  /** Lower a valued Condition by one, removing it at 0; remove an unvalued one. */
  | { kind: "reduce-condition"; id: string }
  | { kind: "clear-condition"; id: string }
  /** A recovery check's outcome while dying (the Roll is #19's): the degree of success. */
  | { kind: "dying-recovery"; degree: "criticalFailure" | "failure" | "success" | "criticalSuccess" }
  | { kind: "apply-effect"; uuid: string; slug: string; name: string; duration: EffectDuration; level?: number | null; badge?: number | null }
  | { kind: "end-effect"; id: string }
  | { kind: "set-badge"; id: string; value: number | null }
  | { kind: "advance"; seconds: number }
  | { kind: "start-encounter" }
  | { kind: "end-encounter" }
  /** On a staff Entry (#21) `rank` is the spell's rank, the charges the casting costs; cantrips cost nothing. */
  | { kind: "spend-slot"; entry: string; rank: string; index?: number }
  | { kind: "restore-slot"; entry: string; rank: string; index?: number }
  | { kind: "spend-innate"; entry: string; spell: string }
  | { kind: "restore-innate"; entry: string; spell: string }
  | { kind: "spend-focus" }
  | { kind: "refocus" }
  | { kind: "spend-hero-point" }
  | { kind: "gain-hero-point" }
  | { kind: "spend-resource"; key: string; amount?: number }
  | { kind: "restore-resource"; key: string; amount?: number }
  | { kind: "use-entry"; uuid: string }
  | { kind: "restore-entry"; uuid: string }
  | { kind: "use-consumable"; id: string }
  | { kind: "fire-ammunition"; id: string; count?: number }
  | { kind: "add-consumable-stack"; stack: Omit<ConsumableStack, "id"> }
  | { kind: "toggle"; key: string; state: ToggleState | null }
  | { kind: "daily-preparations"; at: string }
  | { kind: "start-fresh"; at: string }
  /**
   * A Roll (#19) with what it did to the Session: the Hero Point a reroll spent, the ammunition a
   * strike fired, an Effect a Rule Element ends after the roll, a recovery check's outcome. Reversing
   * it does not un-roll: the record stays, its effects are undone.
   */
  | { kind: "roll"; roll: RollRecord; effects: TableAction[] };

export type PlayAction = PrimitiveAction | TableAction;

export type SessionEventKind =
  | "hp" | "temp-hp" | "iwr" | "condition" | "effect" | "expired" | "dying" | "dead" | "massive-damage" | "time" | "encounter"
  | "slot" | "focus" | "hero-point" | "resource" | "use" | "consumable" | "toggle" | "rest" | "fresh" | "blocked" | "roll-needed" | "roll";

export interface SessionEvent {
  kind: SessionEventKind;
  text: string;
}

export interface ApplyResult {
  session: Session;
  /** Applying this to `session` gives back the Session the action was applied to. */
  inverse: PlayAction;
  events: SessionEvent[];
  /** Set when the action could not be applied (a locked Condition, an unknown id); the Session is unchanged. */
  blocked: string | null;
}

export const PRIMITIVE_KINDS: readonly PrimitiveAction["kind"][] = [
  "set-hp", "add-condition", "remove-condition", "set-condition-value", "add-effect", "remove-effect", "set-effect", "set-hero-points", "set-focus",
  "set-resource", "set-uses", "set-slots", "set-prepared", "set-innate", "set-charges", "add-consumable", "remove-consumable", "set-consumable", "set-clock",
  "set-toggle", "set-counter", "replace", "batch",
];
export const TABLE_KINDS: readonly TableAction["kind"][] = [
  "damage", "heal", "set-temp-hp", "apply-condition", "reduce-condition", "clear-condition", "dying-recovery", "apply-effect", "end-effect", "set-badge",
  "advance", "start-encounter", "end-encounter", "spend-slot", "restore-slot", "spend-innate", "restore-innate", "spend-focus", "refocus", "spend-hero-point",
  "gain-hero-point", "spend-resource", "restore-resource", "use-entry", "restore-entry", "use-consumable", "fire-ammunition", "add-consumable-stack", "toggle",
  "daily-preparations", "start-fresh", "roll",
];
const PRIMITIVE_KIND_SET = new Set<string>(PRIMITIVE_KINDS);
/** Every Play Action kind, for validating a stored Roll Log entry. */
export const PLAY_ACTION_KINDS = new Set<string>([...PRIMITIVE_KINDS, ...TABLE_KINDS]);

export function isPrimitive(action: PlayAction): action is PrimitiveAction {
  return PRIMITIVE_KIND_SET.has(action.kind);
}

/* ---------------------------------- Primitives ---------------------------------- */

/** Apply one primitive. Returns the changed Session and the exact inverse, or null when the id names nothing (or, for an add, is already taken). */
function applyPrimitive(session: Session, action: PrimitiveAction, maxima: SessionMaxima | null): { session: Session; inverse: PrimitiveAction } | null {
  switch (action.kind) {
    case "set-hp":
      return { session: { ...session, hp: { value: action.value, temp: action.temp } }, inverse: { kind: "set-hp", value: session.hp.value, temp: session.hp.temp } };
    case "add-condition": {
      if (session.conditions.some((c) => c.id === action.condition.id)) return null;
      const conditions = [...session.conditions];
      const at = Math.min(Math.max(0, action.index), conditions.length);
      conditions.splice(at, 0, structuredClone(action.condition));
      return { session: { ...session, conditions }, inverse: { kind: "remove-condition", id: action.condition.id } };
    }
    case "remove-condition": {
      const index = session.conditions.findIndex((c) => c.id === action.id);
      if (index === -1) return null;
      const condition = session.conditions[index]!;
      return { session: { ...session, conditions: session.conditions.filter((c) => c.id !== action.id) }, inverse: { kind: "add-condition", condition: structuredClone(condition), index } };
    }
    case "set-condition-value": {
      const condition = findCondition(session, action.id);
      if (!condition) return null;
      return {
        session: { ...session, conditions: session.conditions.map((c) => (c.id === action.id ? { ...c, value: action.value } : c)) },
        inverse: { kind: "set-condition-value", id: action.id, value: condition.value },
      };
    }
    case "add-effect": {
      if (session.effects.some((e) => e.id === action.effect.id)) return null;
      const effects = [...session.effects];
      const at = Math.min(Math.max(0, action.index), effects.length);
      effects.splice(at, 0, structuredClone(action.effect));
      return { session: { ...session, effects }, inverse: { kind: "remove-effect", id: action.effect.id } };
    }
    case "remove-effect": {
      const index = session.effects.findIndex((e) => e.id === action.id);
      if (index === -1) return null;
      return { session: { ...session, effects: session.effects.filter((e) => e.id !== action.id) }, inverse: { kind: "add-effect", effect: structuredClone(session.effects[index]!), index } };
    }
    case "set-effect": {
      const effect = findEffect(session, action.id);
      if (!effect) return null;
      return {
        session: { ...session, effects: session.effects.map((e) => (e.id === action.id ? { ...structuredClone(action.effect), id: action.id } : e)) },
        inverse: { kind: "set-effect", id: action.id, effect: structuredClone(effect) },
      };
    }
    case "set-hero-points":
      return { session: { ...session, heroPoints: action.value }, inverse: { kind: "set-hero-points", value: session.heroPoints } };
    case "set-focus":
      return { session: { ...session, focus: action.value }, inverse: { kind: "set-focus", value: session.focus } };
    case "set-resource": {
      const previous = session.resources[action.key];
      const resources = { ...session.resources };
      if (action.value === null) delete resources[action.key];
      else resources[action.key] = action.value;
      return { session: { ...session, resources }, inverse: { kind: "set-resource", key: action.key, value: previous === undefined ? null : previous } };
    }
    case "set-uses": {
      const uses = { ...session.uses };
      const previous = uses[action.uuid] ?? 0;
      if (action.value === 0) delete uses[action.uuid];
      else uses[action.uuid] = action.value;
      return { session: { ...session, uses }, inverse: { kind: "set-uses", uuid: action.uuid, value: previous } };
    }
    case "set-slots": {
      const entry = session.spellcasting[action.entry] ?? { slots: {}, prepared: {}, innate: {} };
      const previous = entry.slots[action.rank] ?? 0;
      const slots = { ...entry.slots };
      if (action.expended === 0) delete slots[action.rank];
      else slots[action.rank] = action.expended;
      return { session: { ...session, spellcasting: { ...session.spellcasting, [action.entry]: { ...entry, slots } } }, inverse: { kind: "set-slots", entry: action.entry, rank: action.rank, expended: previous } };
    }
    case "set-charges": {
      const entry = session.spellcasting[action.entry] ?? { slots: {}, prepared: {}, innate: {} };
      const previous = entry.charges ?? 0;
      const next: SpellcastingUse = { ...entry };
      if (action.expended === 0) delete next.charges;
      else next.charges = action.expended;
      return { session: { ...session, spellcasting: { ...session.spellcasting, [action.entry]: next } }, inverse: { kind: "set-charges", entry: action.entry, expended: previous } };
    }
    case "set-prepared": {
      const entry = session.spellcasting[action.entry] ?? { slots: {}, prepared: {}, innate: {} };
      const list = [...(entry.prepared[action.rank] ?? [])];
      const previous = list[action.index] ?? false;
      while (list.length <= action.index) list.push(false);
      list[action.index] = action.expended;
      while (list.length > 0 && !list[list.length - 1]) list.pop();
      const prepared = { ...entry.prepared };
      if (list.length === 0) delete prepared[action.rank];
      else prepared[action.rank] = list;
      return { session: { ...session, spellcasting: { ...session.spellcasting, [action.entry]: { ...entry, prepared } } }, inverse: { kind: "set-prepared", entry: action.entry, rank: action.rank, index: action.index, expended: previous } };
    }
    case "set-innate": {
      const entry = session.spellcasting[action.entry] ?? { slots: {}, prepared: {}, innate: {} };
      const previous = entry.innate[action.spell] ?? 0;
      const innate = { ...entry.innate };
      if (action.expended === 0) delete innate[action.spell];
      else innate[action.spell] = action.expended;
      return { session: { ...session, spellcasting: { ...session.spellcasting, [action.entry]: { ...entry, innate } } }, inverse: { kind: "set-innate", entry: action.entry, spell: action.spell, expended: previous } };
    }
    case "add-consumable": {
      if (session.consumables.some((s) => s.id === action.stack.id)) return null;
      const consumables = [...session.consumables];
      const at = Math.min(Math.max(0, action.index), consumables.length);
      consumables.splice(at, 0, structuredClone(action.stack));
      return { session: { ...session, consumables }, inverse: { kind: "remove-consumable", id: action.stack.id } };
    }
    case "remove-consumable": {
      const index = session.consumables.findIndex((s) => s.id === action.id);
      if (index === -1) return null;
      return { session: { ...session, consumables: session.consumables.filter((s) => s.id !== action.id) }, inverse: { kind: "add-consumable", stack: structuredClone(session.consumables[index]!), index } };
    }
    case "set-consumable": {
      const stack = session.consumables.find((s) => s.id === action.id);
      if (!stack) return null;
      const next: ConsumableStack = { ...stack, quantity: action.quantity, uses: stack.uses && action.uses !== null ? { ...stack.uses, value: action.uses } : stack.uses };
      return { session: { ...session, consumables: session.consumables.map((s) => (s.id === action.id ? next : s)) }, inverse: { kind: "set-consumable", id: action.id, quantity: stack.quantity, uses: stack.uses ? stack.uses.value : null } };
    }
    case "set-clock":
      return { session: { ...session, clock: action.clock, round: action.round }, inverse: { kind: "set-clock", clock: session.clock, round: session.round } };
    case "set-toggle": {
      const toggles = { ...session.toggles };
      const previous = toggles[action.key];
      if (action.state === null) delete toggles[action.key];
      else toggles[action.key] = { ...action.state };
      return { session: { ...session, toggles }, inverse: { kind: "set-toggle", key: action.key, state: previous ? { ...previous } : null } };
    }
    case "set-counter":
      return { session: { ...session, counter: action.value }, inverse: { kind: "set-counter", value: session.counter } };
    case "replace":
      return { session: structuredClone(action.session), inverse: { kind: "replace", session: structuredClone(session) } };
    case "batch": {
      let current = session;
      const inverses: PlayAction[] = [];
      for (const step of action.steps) {
        const result = applyPlayActionInner(current, maxima, step);
        if (result.blocked) return null;
        current = result.session;
        inverses.unshift(result.inverse);
      }
      return { session: current, inverse: { kind: "batch", label: action.label, steps: inverses } };
    }
  }
}

/* ------------------------------------ Builder ------------------------------------ */

/** Accumulates primitives for one table action, collecting the reversed inverses and the events. */
class Steps {
  session: Session;
  readonly inverses: PlayAction[] = [];
  readonly events: SessionEvent[] = [];
  blocked: string | null = null;

  constructor(session: Session, readonly maxima: SessionMaxima) {
    this.session = session;
  }

  run(primitive: PrimitiveAction): void {
    if (this.blocked) return;
    const result = applyPrimitive(this.session, primitive, this.maxima);
    if (!result) {
      this.blocked = `${primitive.kind}: nothing to change`;
      return;
    }
    this.session = result.session;
    this.inverses.unshift(result.inverse);
  }

  event(kind: SessionEventKind, text: string): void {
    this.events.push({ kind, text });
  }

  block(reason: string): void {
    this.blocked = reason;
    this.events.push({ kind: "blocked", text: reason });
  }

  allocate(prefix: string): string {
    const used = new Set([...this.session.conditions.map((c) => c.id), ...this.session.effects.map((e) => e.id), ...this.session.consumables.map((s) => s.id)]);
    let counter = this.session.counter;
    while (used.has(`${prefix}${counter}`)) counter++;
    this.run({ kind: "set-counter", value: counter + 1 });
    return `${prefix}${counter}`;
  }

  /* ---- shared pieces ---- */

  setHP(value: number, temp: number): void {
    if (value !== this.session.hp.value || temp !== this.session.hp.temp) this.run({ kind: "set-hp", value, temp });
  }

  /** Add a Condition from the conditions Pack by slug. Returns its id, or null when the Pack lacks it. */
  addCondition(slug: string, value: number | null, persistent: PersistentDamage | null = null, grant: SessionCondition["grant"] = null): string | null {
    const known = this.maxima.conditions[slug];
    if (!known) {
      this.block(`the conditions Pack has no "${slug}"`);
      return null;
    }
    const id = this.allocate("c");
    const condition: SessionCondition = { id, uuid: known.uuid, slug, name: known.name, value: known.valued ? Math.max(1, value ?? 1) : null, grant, persistent };
    this.run({ kind: "add-condition", condition, index: this.session.conditions.length });
    this.event("condition", condition.value === null ? `${condition.name} applied` : `${condition.name} ${condition.value}`);
    return id;
  }

  /** Raise (or lower) a valued Condition to `value`, adding or removing it as needed. */
  setConditionValue(slug: string, value: number, cap: number): void {
    const existing = this.session.conditions.filter((c) => c.slug === slug).sort((a, b) => (b.value ?? 0) - (a.value ?? 0))[0];
    const next = Math.min(Math.max(0, value), cap);
    if (next === 0) {
      if (existing) this.removeCondition(existing.id, true);
      return;
    }
    if (!existing) {
      this.addCondition(slug, next);
      return;
    }
    if (existing.value !== next) {
      this.run({ kind: "set-condition-value", id: existing.id, value: next });
      this.event("condition", `${existing.name} ${next}`);
    }
  }

  /** Remove a Condition and, unless detached, everything it granted (Foundry's cascade). */
  removeCondition(id: string, force = false): void {
    const condition = findCondition(this.session, id);
    if (!condition) return this.block("no such Condition");
    if (!force && isLocked(this.session, condition)) return this.block(`${condition.name} is held in place by what applied it`);
    this.removeGrantsOf(id);
    this.run({ kind: "remove-condition", id });
    this.event("condition", `${condition.name} removed`);
  }

  removeEffect(id: string, why = "removed"): void {
    const effect = findEffect(this.session, id);
    if (!effect) return this.block("no such Effect");
    if (why === "removed" && isLocked(this.session, effect)) return this.block(`${effect.name} is held in place by what applied it`);
    this.removeGrantsOf(id);
    this.run({ kind: "remove-effect", id });
    this.event(why === "expired" ? "expired" : "effect", `${effect.name} ${why}`);
  }

  private removeGrantsOf(granterId: string): void {
    for (const c of this.session.conditions.filter((c) => c.grant?.grantedBy === granterId && c.id !== granterId)) {
      if (c.grant!.onDelete.granter === "detach") {
        // Detached: stays as if applied by hand.
        const index = this.session.conditions.findIndex((x) => x.id === c.id);
        this.run({ kind: "remove-condition", id: c.id });
        this.run({ kind: "add-condition", condition: { ...c, grant: null }, index });
      } else this.removeCondition(c.id, true);
    }
    for (const e of this.session.effects.filter((e) => e.grant?.grantedBy === granterId && e.id !== granterId)) {
      if (e.grant!.onDelete.granter === "detach") this.run({ kind: "set-effect", id: e.id, effect: { ...e, grant: null } });
      else this.removeEffect(e.id, "removed with its source");
    }
  }

  /** Foundry: dying max is 4 (or as raised) less the doomed value. */
  dyingMax(): number {
    return Math.max(0, this.maxima.death.dying - conditionValue(this.session, "doomed"));
  }

  /** Reaching 0 HP: dying 1 + wounded (2 + wounded on a critical hit), or one more (two) while already dying. */
  fallDying(critical: boolean): void {
    const wounded = conditionValue(this.session, "wounded");
    const dying = conditionValue(this.session, "dying");
    const next = dying > 0 ? dying + (critical ? 2 : 1) : (critical ? 2 : 1) + wounded;
    const max = this.dyingMax();
    this.setConditionValue("dying", Math.min(next, Math.max(1, max)), Math.max(1, DEATH_CAPS.dying + 1));
    this.event("dying", `Dying ${Math.min(next, Math.max(1, max))}`);
    if (next >= max) this.event("dead", "Dying reached its maximum: the Character is dead");
  }

  /** Leaving dying: the Condition goes and wounded rises by one. */
  recoverFromDying(): void {
    const dying = this.session.conditions.find((c) => c.slug === "dying");
    if (!dying) return;
    this.removeCondition(dying.id, true);
    this.setConditionValue("wounded", conditionValue(this.session, "wounded") + 1, DEATH_CAPS.wounded);
  }

  /** Expire Effects that have run out at the current clock. */
  expireEffects(): void {
    for (const effect of [...this.session.effects]) {
      if (remainingDuration(effect, this.session.clock).expired) this.removeEffect(effect.id, "expired");
    }
  }

  /** A round begins (Foundry's turn-start events): temporary HP renews, fast healing heals. */
  startOfTurn(): void {
    for (const t of this.maxima.tempHPOnTurnStart) {
      if (t.enabled && t.value > this.session.hp.temp) {
        this.setHP(this.session.hp.value, t.value);
        this.event("temp-hp", `Temporary HP ${t.value} from ${t.sourceName ?? "an Entry"}`);
      }
    }
    for (const f of this.maxima.fastHealing) {
      if (!f.enabled) continue;
      if (typeof f.value !== "number") {
        this.event("roll-needed", `Fast healing ${f.value} (${f.sourceName ?? "an Entry"}) needs a Roll`);
        continue;
      }
      const max = this.maxima.hp.max;
      if (this.session.hp.value >= max || f.value <= 0) continue;
      const healed = Math.min(f.value, max - this.session.hp.value);
      this.setHP(this.session.hp.value + healed, this.session.hp.temp);
      this.event("hp", `Fast healing restores ${healed} HP (${f.sourceName ?? "an Entry"})`);
      if (this.session.hp.value > 0 && conditionValue(this.session, "dying") > 0) this.recoverFromDying();
    }
  }
}

/* -------------------------------- Table actions -------------------------------- */

/**
 * The damage instances an action names: a Roll's typed instances (what lands now; a total raised
 * to 1 from nothing lands on the first), else one instance of the action's type.
 */
function damageInstances(action: Extract<TableAction, { kind: "damage" }>, amount: number): { instances: DamageInstanceInput[]; described: string } {
  const own = (action.instances ?? []).filter((i) => !i.persistent).map((i) => ({ ...i, total: Math.max(0, Math.trunc(i.total)) }));
  if (own.length === 0) {
    const type = action.type && action.type !== "untyped" ? action.type : null;
    return { instances: [{ type: type ?? "untyped", total: amount }], described: type ? `${amount} ${type}` : String(amount) };
  }
  const sum = own.reduce((total, i) => total + i.total, 0);
  if (sum < amount) own[0]!.total += amount - sum;
  return { instances: own, described: own.map((i) => `${i.total}${i.type === "untyped" ? "" : ` ${i.type}`}`).join(" + ") };
}

function applyTable(s: Steps, action: TableAction): void {
  const session = () => s.session;
  const m = s.maxima;
  switch (action.kind) {
    case "roll": {
      s.event("roll", summarizeRoll(action.roll));
      for (const effect of action.effects) {
        applyTable(s, effect);
        if (s.blocked) return;
      }
      return;
    }
    case "damage": {
      const requested = Math.max(0, Math.trunc(action.amount));
      const { instances, described } = damageInstances(action, requested);
      const iwr = applyIWR(m, instances, action.options ?? [], action.critical === true);
      for (const application of iwr.applications) s.event("iwr", describeApplication(application));
      const amount = iwr.applications.length > 0 ? iwr.total : requested;
      const { value, temp } = session().hp;
      const fromTemp = Math.min(temp, amount);
      const toHP = Math.min(value, amount - fromTemp);
      const next = value - toHP;
      s.setHP(next, temp - fromTemp);
      const applied = fromTemp + toHP;
      const taken = amount === requested ? `${described} damage` : `${described} damage, ${amount} after immunities, weaknesses and resistances`;
      s.event("hp", `${taken}${action.source ? ` from ${action.source}` : ""}: ${fromTemp > 0 ? `${fromTemp} to temporary HP, ` : ""}HP ${value} → ${next}`);
      if (applied > 0 && next === 0 && m.hp.max > 0) {
        // Foundry counts the whole amount beyond temporary HP, not only what was left to lose.
        if (amount >= m.hp.max * 2) s.event("massive-damage", `${amount} damage is at least twice the maximum HP: massive damage kills outright`);
        s.fallDying(action.critical === true);
      } else if (amount > 0 && value === 0 && conditionValue(session(), "dying") > 0) {
        // Damage while already at 0 and dying raises dying even when nothing was left to lose.
        s.fallDying(action.critical === true);
      }
      return;
    }
    case "heal": {
      const amount = Math.max(0, Math.trunc(action.amount));
      const { value, temp } = session().hp;
      const next = Math.min(m.hp.max, value + amount);
      s.setHP(next, temp);
      s.event("hp", `${amount} healing${action.source ? ` from ${action.source}` : ""}: HP ${value} → ${next}`);
      if (next > 0 && conditionValue(session(), "dying") > 0) s.recoverFromDying();
      return;
    }
    case "set-temp-hp": {
      const value = Math.max(0, Math.trunc(action.value));
      s.setHP(session().hp.value, value);
      s.event("temp-hp", `Temporary HP ${value}${action.source ? ` from ${action.source}` : ""}`);
      return;
    }
    case "apply-condition": {
      const known = m.conditions[action.slug];
      if (!known) return s.block(`the conditions Pack has no "${action.slug}"`);
      if (action.slug === "persistent-damage" && !action.persistent) return s.block("persistent damage needs a formula and a damage type");
      const cap = action.slug === "dying" ? Math.max(1, s.dyingMax()) : action.slug === "wounded" ? m.death.wounded : action.slug === "doomed" ? m.death.doomed : Number.MAX_SAFE_INTEGER;
      const existing = action.persistent
        ? session().conditions.find((c) => c.persistent && conditionKey(c) === conditionKey({ slug: action.slug, persistent: action.persistent! }))
        : session().conditions.filter((c) => c.slug === action.slug).sort((a, b) => (b.value ?? 0) - (a.value ?? 0))[0];
      if (existing && known.valued) {
        const next = Math.min(cap, Math.max(1, action.set ? (action.value ?? 1) : (existing.value ?? 0) + (action.value ?? 1)));
        if (next !== existing.value) {
          s.run({ kind: "set-condition-value", id: existing.id, value: next });
          s.event("condition", `${existing.name} ${next}`);
        }
      } else if (existing && !action.persistent) {
        s.event("condition", `${existing.name} is already applied`);
      } else {
        s.addCondition(action.slug, known.valued ? Math.min(cap, action.value ?? 1) : null, action.persistent ?? null);
      }
      if (action.slug === "dying" && conditionValue(session(), "dying") >= s.dyingMax()) s.event("dead", "Dying reached its maximum: the Character is dead");
      return;
    }
    case "reduce-condition": {
      const condition = findCondition(session(), action.id);
      if (!condition) return s.block("no such Condition");
      if (isLocked(session(), condition)) return s.block(`${condition.name} is held in place by what applied it`);
      if (condition.value !== null && condition.value > 1) {
        s.run({ kind: "set-condition-value", id: condition.id, value: condition.value - 1 });
        s.event("condition", `${condition.name} ${condition.value - 1}`);
      } else {
        s.removeCondition(condition.id);
        if (condition.slug === "dying") s.setConditionValue("wounded", conditionValue(session(), "wounded") + 1, DEATH_CAPS.wounded);
      }
      return;
    }
    case "clear-condition": {
      const condition = findCondition(session(), action.id);
      if (!condition) return s.block("no such Condition");
      s.removeCondition(action.id);
      if (!s.blocked && condition.slug === "dying") s.setConditionValue("wounded", conditionValue(session(), "wounded") + 1, DEATH_CAPS.wounded);
      return;
    }
    case "dying-recovery": {
      const dying = conditionValue(session(), "dying");
      if (dying === 0) return s.block("the Character is not dying");
      const delta = { criticalSuccess: -2, success: -1, failure: 1, criticalFailure: 2 }[action.degree];
      const next = dying + delta;
      if (next <= 0) {
        s.recoverFromDying();
        s.event("dying", "Recovered from dying");
      } else {
        s.setConditionValue("dying", Math.min(next, Math.max(1, s.dyingMax())), DEATH_CAPS.dying + 1);
        s.event("dying", `Dying ${Math.min(next, Math.max(1, s.dyingMax()))}`);
        if (next >= s.dyingMax()) s.event("dead", "Dying reached its maximum: the Character is dead");
      }
      return;
    }
    case "apply-effect": {
      const id = s.allocate("e");
      const effect: SessionEffect = {
        id,
        uuid: action.uuid,
        slug: action.slug,
        name: action.name,
        level: action.level ?? null,
        badge: action.badge ?? null,
        start: session().clock,
        duration: { ...action.duration },
        grant: null,
      };
      s.run({ kind: "add-effect", effect, index: session().effects.length });
      s.event("effect", `${effect.name} applied`);
      return;
    }
    case "end-effect":
      return s.removeEffect(action.id);
    case "set-badge": {
      const effect = findEffect(session(), action.id);
      if (!effect) return s.block("no such Effect");
      s.run({ kind: "set-effect", id: action.id, effect: { ...effect, badge: action.value } });
      s.event("effect", `${effect.name} ${action.value ?? ""}`.trim());
      return;
    }
    case "advance": {
      const seconds = Math.max(0, Math.trunc(action.seconds));
      const inEncounter = session().round !== null;
      const rounds = Math.floor(seconds / ROUND_SECONDS);
      if (inEncounter && seconds >= ROUND_SECONDS) {
        // Round by round, so turn-start events and expiries land in order.
        for (let i = 0; i < rounds; i++) {
          s.run({ kind: "set-clock", clock: session().clock + ROUND_SECONDS, round: (session().round ?? 0) + 1 });
          s.expireEffects();
          s.startOfTurn();
        }
        const rest = seconds - rounds * ROUND_SECONDS;
        if (rest > 0) s.run({ kind: "set-clock", clock: session().clock + rest, round: session().round });
        s.event("time", rounds === 1 ? `Round ${session().round}` : `${rounds} rounds pass (round ${session().round})`);
      } else {
        s.run({ kind: "set-clock", clock: session().clock + seconds, round: session().round });
        s.event("time", describeSpan(seconds) + " pass");
      }
      s.expireEffects();
      return;
    }
    case "start-encounter": {
      if (session().round !== null) return s.block("an encounter is already running");
      s.run({ kind: "set-clock", clock: session().clock, round: 1 });
      s.event("encounter", "Encounter begins (round 1)");
      s.startOfTurn();
      return;
    }
    case "end-encounter": {
      if (session().round === null) return s.block("no encounter is running");
      s.run({ kind: "set-clock", clock: session().clock, round: null });
      for (const effect of [...session().effects]) if (effect.duration.unit === "encounter") s.removeEffect(effect.id, "ended with the encounter");
      s.event("encounter", "Encounter ends");
      return;
    }
    case "spend-slot":
    case "restore-slot": {
      const entry = m.spellcasting.find((e) => e.id === action.entry);
      if (!entry) return s.block("no such Spellcasting Entry");
      const use = session().spellcasting[action.entry] ?? { slots: {}, prepared: {}, innate: {} };
      const spend = action.kind === "spend-slot";
      if (entry.charges) {
        // A staff: a spell costs charges equal to its rank; a cantrip costs nothing and is not recorded.
        const cost = Number(action.rank);
        if (!Number.isInteger(cost) || !entry.charges.spells.some((sp) => sp.rank === cost)) return s.block(`${entry.name} has no rank ${action.rank} spells`);
        if (cost === 0) return;
        const expended = use.charges ?? 0;
        if (spend && expended + cost > entry.charges.max) return s.block(`${entry.name} has ${entry.charges.max - expended} charge${entry.charges.max - expended === 1 ? "" : "s"} left; a rank ${cost} spell costs ${cost}`);
        if (!spend && expended - cost < 0) return s.block(`${entry.name} has not spent ${cost} charges`);
        const next = expended + (spend ? cost : -cost);
        s.run({ kind: "set-charges", entry: action.entry, expended: next });
        s.event("slot", `${entry.name}: charges ${entry.charges.max - next} / ${entry.charges.max}`);
        return;
      }
      const slot = entry.slots[action.rank];
      if (!slot) return s.block(`${entry.name} has no rank ${action.rank} slots`);
      if (entry.casting === "prepared") {
        const index = action.index ?? (slot.prepared ?? []).findIndex((sp, i) => sp !== null && (use.prepared[action.rank]?.[i] ?? false) === !spend);
        if (index < 0 || index >= slot.max) return s.block(spend ? `${entry.name} has no unspent rank ${action.rank} spell prepared` : `${entry.name} has no spent rank ${action.rank} spell`);
        if ((use.prepared[action.rank]?.[index] ?? false) === spend) return s.block(spend ? "that prepared spell is already cast" : "that prepared spell is not cast");
        s.run({ kind: "set-prepared", entry: action.entry, rank: action.rank, index, expended: spend });
        s.event("slot", `${entry.name}: rank ${action.rank} slot ${index + 1} ${spend ? "cast" : "restored"}`);
      } else {
        const expended = use.slots[action.rank] ?? 0;
        if (spend && expended >= slot.max) return s.block(`${entry.name} has no rank ${action.rank} slots left`);
        if (!spend && expended <= 0) return s.block(`${entry.name} has every rank ${action.rank} slot`);
        s.run({ kind: "set-slots", entry: action.entry, rank: action.rank, expended: expended + (spend ? 1 : -1) });
        s.event("slot", `${entry.name}: rank ${action.rank} slots ${slot.max - expended - (spend ? 1 : -1)} / ${slot.max}`);
      }
      return;
    }
    case "spend-innate":
    case "restore-innate": {
      const entry = m.spellcasting.find((e) => e.id === action.entry);
      const innate = entry?.innate[action.spell];
      if (!entry || !innate) return s.block("no such innate spell");
      const use = session().spellcasting[action.entry] ?? { slots: {}, prepared: {}, innate: {} };
      const expended = use.innate[action.spell] ?? 0;
      const spend = action.kind === "spend-innate";
      if (spend && expended >= innate.max) return s.block(`${innate.name} has no uses left`);
      if (!spend && expended <= 0) return s.block(`${innate.name} has every use`);
      s.run({ kind: "set-innate", entry: action.entry, spell: action.spell, expended: expended + (spend ? 1 : -1) });
      s.event("slot", `${innate.name}: ${innate.max - expended - (spend ? 1 : -1)} / ${innate.max} uses`);
      return;
    }
    case "spend-focus": {
      if (session().focus <= 0) return s.block("no Focus Points left");
      s.run({ kind: "set-focus", value: session().focus - 1 });
      s.event("focus", `Focus Points ${session().focus} / ${m.focus.max}`);
      return;
    }
    case "refocus": {
      if (session().focus >= m.focus.max) return s.block("the focus pool is full");
      s.run({ kind: "set-focus", value: session().focus + 1 });
      s.event("focus", `Refocus: Focus Points ${session().focus} / ${m.focus.max}`);
      return;
    }
    case "spend-hero-point": {
      if (session().heroPoints <= 0) return s.block("no Hero Points left");
      s.run({ kind: "set-hero-points", value: session().heroPoints - 1 });
      s.event("hero-point", `Hero Points ${session().heroPoints}`);
      return;
    }
    case "gain-hero-point": {
      const max = Math.min(m.heroPoints.max, HERO_POINTS_MAX);
      if (session().heroPoints >= max) return s.block(`Hero Points are capped at ${max}`);
      s.run({ kind: "set-hero-points", value: session().heroPoints + 1 });
      s.event("hero-point", `Hero Points ${session().heroPoints}`);
      return;
    }
    case "spend-resource":
    case "restore-resource": {
      const resource = m.resources[action.key];
      if (!resource) return s.block("no such resource");
      const current = session().resources[action.key] ?? resource.max;
      const amount = Math.max(1, Math.trunc(action.amount ?? 1));
      const next = action.kind === "spend-resource" ? current - amount : current + amount;
      if (next < 0) return s.block(`${resource.label}: only ${current} left`);
      if (next > resource.max) return s.block(`${resource.label} is capped at ${resource.max}`);
      s.run({ kind: "set-resource", key: action.key, value: next });
      s.event("resource", `${resource.label} ${next} / ${resource.max}`);
      return;
    }
    case "use-entry":
    case "restore-entry": {
      const entry = m.uses[action.uuid];
      if (!entry) return s.block("that Entry has no frequency");
      const spent = session().uses[action.uuid] ?? 0;
      if (action.kind === "use-entry" && spent >= entry.max) return s.block(`${entry.name} has no uses left`);
      if (action.kind === "restore-entry" && spent <= 0) return s.block(`${entry.name} has every use`);
      const next = spent + (action.kind === "use-entry" ? 1 : -1);
      s.run({ kind: "set-uses", uuid: action.uuid, value: next });
      s.event("use", `${entry.name}: ${entry.max - next} / ${entry.max} uses (${describePer(entry.per)})`);
      return;
    }
    case "use-consumable":
    case "fire-ammunition": {
      const stack = session().consumables.find((c) => c.id === action.id);
      if (!stack) return s.block("no such stack");
      const consume = (count: number): void => {
        // One item of the stack per count; the stack goes at 0 when it auto-destroys.
        const quantity = stack.quantity - count;
        if (quantity <= 0 && stack.autoDestroy) s.run({ kind: "remove-consumable", id: stack.id });
        else s.run({ kind: "set-consumable", id: stack.id, quantity: Math.max(0, quantity), uses: stack.uses ? stack.uses.max : null });
        s.event("consumable", quantity <= 0 && stack.autoDestroy ? `${stack.name} used up` : `${stack.name}: ${Math.max(0, quantity)} left`);
      };
      if (action.kind === "fire-ammunition" || !stack.uses) {
        const count = action.kind === "fire-ammunition" ? Math.max(1, Math.trunc(action.count ?? 1)) : 1;
        if (stack.quantity < count) return s.block(`${stack.name}: ${stack.quantity === 0 ? "none" : `only ${stack.quantity}`} left`);
        return consume(count);
      }
      // Foundry's `ConsumablePF2e#consume`: uses first; the last use spends one item of an auto-destroying stack, else leaves it at 0 uses.
      if (stack.uses.value <= 0 || stack.quantity <= 0) return s.block(`${stack.name}: none left`);
      if (stack.uses.value > 1) {
        s.run({ kind: "set-consumable", id: stack.id, quantity: stack.quantity, uses: stack.uses.value - 1 });
        s.event("consumable", `${stack.name}: ${stack.uses.value - 1} / ${stack.uses.max} uses`);
        return;
      }
      if (stack.autoDestroy) return consume(1);
      s.run({ kind: "set-consumable", id: stack.id, quantity: stack.quantity, uses: 0 });
      s.event("consumable", `${stack.name}: 0 / ${stack.uses.max} uses`);
      return;
    }
    case "add-consumable-stack": {
      const id = s.allocate("s");
      s.run({ kind: "add-consumable", stack: { ...structuredClone(action.stack), id }, index: session().consumables.length });
      s.event("consumable", `${action.stack.name} ×${action.stack.quantity} added`);
      return;
    }
    case "toggle": {
      s.run({ kind: "set-toggle", key: action.key, state: action.state });
      s.event("toggle", `${action.key}: ${action.state === null ? "as the Character has it" : action.state.checked === false ? "off" : "on"}${action.state?.selection ? ` (${action.state.selection})` : ""}`);
      return;
    }
    case "daily-preparations":
      return dailyPreparations(s, action.at);
    case "start-fresh": {
      s.run({ kind: "replace", session: freshSession(m, action.at) });
      s.event("fresh", "Session started fresh");
      return;
    }
  }
}

/** Foundry's rest-for-the-night, then its daily recharge, then what this app adds (the clock, until-rest Effects). */
function dailyPreparations(s: Steps, at: string): void {
  const m = s.maxima;
  const session = () => s.session;
  s.event("rest", `Daily preparations (${at.slice(0, 10)})`);
  // Hit points: max(Con, 1) × Level × multiplier + addend.
  const restored = Math.max(m.conMod, 1) * m.level * m.hp.recoveryMultiplier + m.hp.recoveryAddend;
  const lost = m.hp.max - session().hp.value;
  const healed = Math.max(0, Math.min(lost, restored));
  if (healed > 0) {
    s.setHP(session().hp.value + healed, session().hp.temp);
    s.event("hp", `Rest restores ${healed} HP (${session().hp.value} / ${m.hp.max})`);
  }
  // Conditions: fatigued goes; doomed and drained drop by one; wounded goes at full HP.
  const fatigued = session().conditions.find((c) => c.slug === "fatigued");
  if (fatigued) s.removeCondition(fatigued.id, true);
  for (const slug of ["doomed", "drained"]) {
    const value = conditionValue(session(), slug);
    if (value > 0) s.setConditionValue(slug, value - 1, DEATH_CAPS.doomed);
  }
  if (session().hp.value >= m.hp.max) {
    const wounded = session().conditions.find((c) => c.slug === "wounded");
    if (wounded) s.removeCondition(wounded.id, true);
  }
  // Recharge (Foundry: `recharge({ duration: "day" })`): frequencies of a day or less, spell slots, focus, daily resources.
  for (const [uuid, use] of Object.entries(m.uses)) {
    if ((session().uses[uuid] ?? 0) > 0 && renewsDaily(use.per)) s.run({ kind: "set-uses", uuid, value: 0 });
  }
  let slotsReset = false;
  for (const [entry, use] of Object.entries(session().spellcasting)) {
    for (const [rank, expended] of Object.entries(use.slots)) if (expended > 0) (s.run({ kind: "set-slots", entry, rank, expended: 0 }), (slotsReset = true));
    for (const [rank, list] of Object.entries(use.prepared)) list.forEach((spent, index) => spent && (s.run({ kind: "set-prepared", entry, rank, index, expended: false }), (slotsReset = true)));
    for (const [spell, expended] of Object.entries(use.innate)) if (expended > 0) (s.run({ kind: "set-innate", entry, spell, expended: 0 }), (slotsReset = true));
    // A staff is prepared anew: its charges return (GM Core, Staves).
    if ((use.charges ?? 0) > 0) (s.run({ kind: "set-charges", entry, expended: 0 }), (slotsReset = true));
  }
  if (slotsReset) s.event("slot", "Spell slots, innate uses and staff charges restored");
  if (session().focus < m.focus.max) {
    s.run({ kind: "set-focus", value: m.focus.max });
    s.event("focus", `Focus Points ${m.focus.max} / ${m.focus.max}`);
  }
  for (const [key, resource] of Object.entries(m.resources)) {
    if (resource.renew === "daily" && (session().resources[key] ?? resource.max) < resource.max) {
      s.run({ kind: "set-resource", key, value: resource.max });
      s.event("resource", `${resource.label} ${resource.max} / ${resource.max}`);
    }
  }
  for (const stack of session().consumables) {
    if (stack.uses?.renew === "day" && stack.uses.value < stack.uses.max) {
      s.run({ kind: "set-consumable", id: stack.id, quantity: stack.quantity, uses: stack.uses.max });
      s.event("consumable", `${stack.name} recharged`);
    }
  }
  // Time passes: eight hours of rest. Encounter and until-rest Effects end; timed ones expire on the clock.
  if (session().round !== null) applyTable(s, { kind: "end-encounter" });
  s.run({ kind: "set-clock", clock: session().clock + REST_SECONDS, round: null });
  for (const effect of [...session().effects]) if (effect.duration.unit === "until-rest") s.removeEffect(effect.id, "ended at daily preparations");
  s.expireEffects();
}

/** Foundry: a frequency of a day, a turn or a round, or an ISO duration of eight hours or less, refreshes at daily preparations. */
export function renewsDaily(per: string): boolean {
  if (per === "day" || per === "turn" || per === "round") return true;
  const seconds = isoSeconds(per);
  return seconds !== null && seconds <= 8 * 3600;
}

function isoSeconds(per: string): number | null {
  const m = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/.exec(per);
  if (!m) return null;
  const [, y, mo, w, d, h, mi, sec] = m.map((v) => (v === undefined ? 0 : Number(v)));
  return y! * 31536000 + mo! * 2592000 + w! * 604800 + d! * 86400 + h! * 3600 + mi! * 60 + sec!;
}

export function describePer(per: string): string {
  const fixed: Record<string, string> = { day: "per day", turn: "per turn", round: "per round", PT1M: "per minute", PT10M: "per 10 minutes", PT1H: "per hour", PT24H: "per 24 hours", P1W: "per week", P1M: "per month", P1Y: "per year" };
  return fixed[per] ?? `per ${per}`;
}

function describeSpan(seconds: number): string {
  if (seconds % 3600 === 0) return `${seconds / 3600} hour${seconds === 3600 ? "" : "s"}`;
  if (seconds % 60 === 0) return `${seconds / 60} minute${seconds === 60 ? "" : "s"}`;
  if (seconds % ROUND_SECONDS === 0) return `${seconds / ROUND_SECONDS} round${seconds === ROUND_SECONDS ? "" : "s"}`;
  return `${seconds} seconds`;
}

/* ----------------------------------- Entry point ----------------------------------- */

function applyPlayActionInner(session: Session, maxima: SessionMaxima | null, action: PlayAction): ApplyResult {
  if (isPrimitive(action)) {
    const result = applyPrimitive(session, action, maxima);
    if (!result) return { session, inverse: { kind: "batch", label: "nothing", steps: [] }, events: [{ kind: "blocked", text: `${action.kind}: nothing to change` }], blocked: `${action.kind}: nothing to change` };
    return { session: result.session, inverse: result.inverse, events: [], blocked: null };
  }
  if (!maxima) return { session, inverse: { kind: "batch", label: "nothing", steps: [] }, events: [], blocked: "a table action needs the Session maxima" };
  const steps = new Steps(session, maxima);
  applyTable(steps, action);
  if (steps.blocked) return { session, inverse: { kind: "batch", label: "nothing", steps: [] }, events: steps.events, blocked: steps.blocked };
  return { session: steps.session, inverse: { kind: "batch", label: `undo ${action.kind}`, steps: steps.inverses }, events: steps.events, blocked: null };
}

/**
 * Apply a Play Action. The input Session is never mutated. A blocked action returns the input
 * Session with `blocked` set and an inverse that changes nothing.
 */
export function applyPlayAction(session: Session, maxima: SessionMaxima, action: PlayAction): ApplyResult {
  return applyPlayActionInner(session, maxima, action);
}
